import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Output } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { I18NextModule } from 'angular-i18next';
import { SharedModule } from '../../shared.module';
import i18next from 'i18next';
import { ImageModalComponent } from '../image-modal/image-modal.component';
import { formatNumber } from '@angular/common';

@Component({
  standalone: true,
  selector: 'app-car-detail-modal',
  templateUrl: './car-detail-modal.component.html',
  styleUrls: ['./car-detail-modal.component.scss'],
  imports: [
    CommonModule,
    I18NextModule,
    MatDialogModule,
    MatIconModule,
    SharedModule
  ]
})
export class CarDetailModalComponent {
  @Output() onReserve = new EventEmitter<any>();

  activeImageIndex: number = 0;
  selectedImage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<CarDetailModalComponent>,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      car: any;
      theme: string;
      requestType: string;
      view?: boolean;
    }
  ) {}

  getImgIndex(): number {
    return this.activeImageIndex;
  }

  nextImg(total: number, event: Event): void {
    event.stopPropagation();
    this.activeImageIndex = (this.activeImageIndex + 1) % total;
  }

  prevImg(total: number, event: Event): void {
    event.stopPropagation();
    this.activeImageIndex = (this.activeImageIndex - 1 + total) % total;
  }

  openImageCarousel(images: string[], selectedIndex: number): void {
    this.dialog.open(ImageModalComponent, {
      data: { images, selectedIndex },
      panelClass: 'image-dialog',
      backdropClass: 'dark-backdrop'
    });
  }

  reserveVehicle() {
    this.onReserve.emit(this.data.car);
    this.dialogRef.close();
  }

  printDetailVO() {
    const car = this.data.car;
    const imagenes = car.vehicleGroups[0]?.image?.split('|') || [];

    const printWindow = window.open('', '_blank');

    const optionals = car.optionals ?? [];
    const optionalsHtml = optionals.length
      ? `<ul class="optionals">
       ${optionals.map((o: any) => `<li>${o.description ?? ''}</li>`).join('')}
     </ul>`
      : '<span class="muted">-</span>';

    const kmsFormatted = formatNumber(
      car.vehicleGroups[0]?.kms,
      'es-ES',
      '1.0-0'
    );

    const priceVNFormatted = formatNumber(
      car.vehicleGroups[0]?.prices?.priceVN,
      'es-ES',
      '1.0-0'
    );

    const priceVOFormatted = formatNumber(
      car.vehicleGroups[0]?.prices?.priceVO,
      'es-ES',
      '1.0-0'
    );

    const registrationDateFormatted = this.datePipe.transform(
      car.plateRegistrationDate,
      'dd/MM/yyyy'
    );

    if (printWindow) {
      const htmlContent = `
      <html>
        <head>
          <title>${i18next.t('technicalSheet')} - ${car.modelName + ' ' + car.engine + ' ' + car.finish}</title>
          <style>
            @font-face {
              font-family: 'SeatRegular';
              src: url('${window.location.origin}/assets/fonts/seat2/SEATBCN-Regular.woff') format('woff');
              font-weight: normal;
              font-style: normal;
            }
            body { font-family: 'SeatRegular'; padding: 20px; color: #333; }
            .header { text-align: center; border-bottom: 2px solid #104861; padding-bottom: 10px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px; }
            .section-title { color: #104861; text-transform: uppercase; font-weight: bold; border-bottom: 1px solid #ddd; margin-top: 20px; }
            .data-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #eee; font-size: 16px;}
            .label { font-weight: bold; font-size: 16px; }
            .full-width-section {
              margin-top: 20px;
              width: 100%;
            }
            .optionals-list {
              margin: 0;
              padding-left: 18px;
            }
            .optionals-list li {
              margin-bottom: 4px;
            }
            .gallery-page {
              page-break-before: always;
              break-before: page;
              margin-top: 0;
            }
            .gallery { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 30px; page-break-before: auto; }
            .gallery img { width: 100%; border-radius: 5px; }
            .vo-info-texts { margin-top: 16px; }
            .vo-info-text { margin: 0 0 10px 0; font-size: 10px; line-height: 1.4; color: #2c2a29; font-weight: bold; font-style: italic; }
            @media print { .no-print { display: none; } .gallery-page { page-break-before: always; break-before: page; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${car.modelName + ' ' + car.engine + ' ' + car.finish}</h1>
          </div>

          <div class="grid">
            <div>
              <div class="section-title">${i18next.t('basicData')}</div>
              <div class="data-row"><span class="label">${i18next.t('plateNumber')}:</span> <span>${car.plateNumber}</span></div>
              <div class="data-row"><span class="label">${i18next.t('vinNumber')}:</span> <span>${car.VIN}</span></div>
              <div class="data-row"><span class="label">${i18next.t('summaryVinNumberDate')}:</span> <span>${registrationDateFormatted}</span></div>
              <div class="data-row"><span class="label">${i18next.t('kms')}:</span> <span>${kmsFormatted}</span></div>
              <div class="data-row"><span class="label">${i18next.t('VNPrice')}:</span> <span>${priceVNFormatted}€</span></div>
              <div class="data-row"><span class="label">${i18next.t('VOPrice')}:</span> <span>${priceVOFormatted}€</span></div>
              <div class="data-row"><span class="label">${i18next.t('discount')}:</span> <span>${car.vehicleGroups[0]?.prices?.discount}%</span></div>
            </div>
            <div>
              <div class="section-title">${i18next.t('features')}</div>
              <div class="data-row"><span class="label">${i18next.t('combustible')}:</span> <span>${car.fuelType}</span></div>
              <div class="data-row"><span class="label">${i18next.t('gearbox')}:</span> <span>${car.transmission}</span></div>
              <div class="data-row"><span class="label">${i18next.t('color')}:</span> <span>${car.exteriorColor}</span></div>
              <div class="data-row"><span class="label">${i18next.t('engine')}:</span> <span>${car.engine}</span></div>
              <div class="data-row"><span class="label">${i18next.t('finishes')}:</span> <span>${car.finish}</span></div>
            </div>
          </div>

          <div class="full-width-section">
            <div class="section-title">${i18next.t('optionals')}</div>
            ${optionalsHtml}
          </div>

          <div class="gallery-page">
            <div class="section-title">${i18next.t('vehicleImages')}</div>
            <div class="gallery">
              ${imagenes.map((img: string) => `<img src="${img}" />`).join('')}
            </div>

            <div class="vo-info-texts">
              <p class="vo-info-text">${i18next.t('infoPriceVO')}</p>
              <p class="vo-info-text">${i18next.t('infoPriceVN')}</p>
            </div>
          </div>

          <script>
            window.onload = () => {
              window.print();
              // Opcional: window.close(); // Cierra la pestaña tras imprimir
            };
          </script>
        </body>
      </html>
    `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  }
}
